@if (hasContent()) {
  <button type="button" cdkOverlayOrigin #origin="cdkOverlayOrigin"
          data-testid="ocr-facts-toggle"
          class="inline-flex items-center justify-center w-7 h-7 rounded-full text-ds-secondary cursor-pointer hover:bg-ds-primary/5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
          [matTooltip]="summary()"
          [attr.aria-label]="summary() + '. Show performance details'"
          aria-haspopup="dialog"
          [attr.aria-expanded]="open()"
          (click)="toggle()">
    <mat-icon aria-hidden="true" class="!text-[18px] !w-[18px] !h-[18px] !leading-none">speed</mat-icon>
  </button>

  <ng-template cdkConnectedOverlay
               [cdkConnectedOverlayOrigin]="origin"
               [cdkConnectedOverlayOpen]="open()"
               [cdkConnectedOverlayPositions]="popoverPositions"
               [cdkConnectedOverlayHasBackdrop]="true"
               cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
               (backdropClick)="close()"
               (detach)="close()">
    <section role="dialog" aria-labelledby="ocr-facts-title" cdkTrapFocus cdkTrapFocusAutoCapture
             data-testid="ocr-facts-panel"
             class="w-96 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto thin-scroll bg-ds-surface rounded-ds-md border border-ds-border shadow-lg p-3">
      <div class="flex items-center justify-between gap-2 mb-2">
        <h3 id="ocr-facts-title" class="text-sm font-semibold font-data text-ds-text flex items-center gap-2 flex-wrap"
            data-testid="ocr-facts-title">
          Performance
          @if (durationSeconds(); as duration) {
            <span class="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold font-data tabular-nums rounded-full bg-ds-secondary/10 text-ds-secondary border border-ds-secondary/30"
                  data-testid="ocr-facts-summary-duration">
              {{ duration }}
            </span>
          }
          @if (costLabel(); as cost) {
            <span class="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold font-data tabular-nums rounded-full bg-ds-accent/10 text-ds-accent border border-ds-accent/30"
                  data-testid="ocr-facts-summary-cost">
              {{ cost }}
            </span>
          }
        </h3>
        <button type="button" aria-label="Close performance details" data-testid="ocr-facts-close-btn"
                class="inline-flex items-center justify-center w-7 h-7 rounded-full text-ds-text-muted hover:bg-ds-primary/5 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                (click)="close()">
          <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">close</mat-icon>
        </button>
      </div>
      <div class="overflow-x-auto thin-scroll">
        <table class="min-w-full text-sm" data-testid="ocr-facts-table">
          <caption class="sr-only">Performance facts recorded for this run</caption>
          <tbody>
            @if (durationSeconds(); as duration) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-duration">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Duration</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ duration }}</td>
              </tr>
            }
            @if (completedAt(); as completed) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-completed-at">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Completed at</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text-muted">{{ completed | date:'medium' }}</td>
              </tr>
            }
            @if (fixAttempts(); as attempts) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-fix-attempts">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Fix attempts</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ attempts }}</td>
              </tr>
            }
            @if (verdict(); as judgeVerdict) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-verdict">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Judge verdict</th>
                <td class="px-2 py-1 text-right font-data text-ds-text">{{ judgeVerdict }}</td>
              </tr>
            }
            @if (judgeConfidence(); as confidence) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-judge-confidence">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Judge confidence</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ confidence }}</td>
              </tr>
            }
            @if (templateMatchScore(); as score) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-template-match-score">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Template match score</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ score }}</td>
              </tr>
            }
            @if (routingOutcome(); as outcome) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-routing-outcome">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Routing outcome</th>
                <td class="px-2 py-1 text-right font-data text-ds-text">{{ outcome }}</td>
              </tr>
            }
            @if (tokens().input; as inputTokens) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-tokens-input">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Input tokens</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ inputTokens }}</td>
              </tr>
            }
            @if (tokens().cachedInput; as cachedTokens) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-tokens-cached">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text"
                    matTooltip="Input tokens served from the prompt cache">Cached input tokens</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text-muted">{{ cachedTokens }}</td>
              </tr>
            }
            @if (tokens().output; as outputTokens) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-tokens-output">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Output tokens</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ outputTokens }}</td>
              </tr>
            }
            @if (tokens().reasoning; as reasoningTokens) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-tokens-reasoning">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Reasoning tokens</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text-muted">{{ reasoningTokens }}</td>
              </tr>
            }
            @if (tokens().total; as totalTokens) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-tokens-total">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Total tokens</th>
                <td class="px-2 py-1 text-right font-data tabular-nums font-semibold text-ds-text">{{ totalTokens }}</td>
              </tr>
            }
            @if (tokens().requests; as requests) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-requests">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Agent requests</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ requests }}</td>
              </tr>
            }
            @if (pagesConverted(); as pages) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-pages-converted">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text"
                    matTooltip="Document pages the OCR vendor converted to markdown and billed for. Zero means the markdown was already cached from an earlier run, so this run was not charged for conversion.">Pages converted</th>
                <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ pages }}</td>
              </tr>
            }
            @if (costLabel(); as cost) {
              <tr class="border-t border-ds-border first:border-t-0" data-testid="ocr-facts-cost">
                <th scope="row" class="px-2 py-1 text-left font-medium text-ds-text">Total cost</th>
                <td class="px-2 py-1 text-right font-data tabular-nums font-semibold text-ds-text">{{ cost }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (costByModelRows().length) {
        <div class="mt-3">
          <h4 class="text-xs font-semibold font-data text-ds-text-muted mb-1"
              data-testid="ocr-facts-cost-by-model-title">
            Cost by model
          </h4>
          <div class="overflow-x-auto thin-scroll">
            <table class="min-w-full text-sm" data-testid="ocr-facts-cost-by-model">
              <thead class="bg-ds-bg text-ds-text-muted">
                <tr>
                  <th scope="col" class="px-2 py-1 text-left text-xs font-medium">Model</th>
                  <th scope="col" class="px-2 py-1 text-right text-xs font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                @for (row of costByModelRows(); track row.modelId) {
                  <tr class="border-t border-ds-border" data-testid="ocr-facts-cost-by-model-row">
                    <td class="px-2 py-1 font-data text-ds-text-muted break-all">{{ row.modelId }}</td>
                    <td class="px-2 py-1 text-right font-data tabular-nums text-ds-text">{{ row.costLabel }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }

      @if (rateCardRevision(); as revision) {
        <p class="mt-3 text-xs font-data text-ds-text-muted" data-testid="ocr-facts-rate-card-revision">
          Priced with rate card {{ revision }}
        </p>
      }
    </section>
  </ng-template>
}
